import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import {
  getPlatformTenant,
  readSession,
  type PlatformTenantDetail,
} from '../../lib/api.js';

type TenantDetailProps = {
  config: ClientConfig;
};

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; status: number; message: string }
  | { kind: 'ready'; tenant: PlatformTenantDetail };

const STATUS_LABEL: Record<string, string> = {
  provisioning: 'Provisioning',
  active: 'Active',
  suspended: 'Suspended',
  deactivating: 'Deactivating',
  deactivated: 'Deactivated',
  purged: 'Purged',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}

export function TenantDetail({ config }: TenantDetailProps): JSX.Element {
  const { tenantId } = useParams<{ tenantId: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  const apiUrl = config.deployment.apiUrl;

  const load = useCallback(async () => {
    if (!tenantId) {
      setState({ kind: 'error', status: 400, message: 'Tenant id missing from URL.' });
      return;
    }
    setState({ kind: 'loading' });
    const { token } = readSession();
    if (!token) {
      setState({ kind: 'error', status: 401, message: 'Not authenticated' });
      return;
    }
    try {
      const tenant = await getPlatformTenant(apiUrl, token, tenantId);
      setState({ kind: 'ready', tenant });
    } catch (err) {
      const status = (err as { status?: number }).status ?? 500;
      const message = err instanceof Error ? err.message : 'Unable to load tenant';
      setState({ kind: 'error', status, message });
    }
  }, [apiUrl, tenantId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="ferro-admin-page" aria-busy={state.kind === 'loading'}>
      <div className="ferro-section__header">
        <h1 className="ferro-section__title">
          {state.kind === 'ready' ? state.tenant.displayName : 'Tenant'}
        </h1>
        <Link to="/platform/tenants" className="ferro-btn ferro-btn--ghost">
          ← Back to tenants
        </Link>
      </div>

      {state.kind === 'loading' ? (
        <p className="ferro-muted" role="status">
          Loading…
        </p>
      ) : state.kind === 'error' ? (
        <p className="ferro-error" role="alert">
          {state.status === 404
            ? 'That tenant does not exist or you cannot view it.'
            : state.message}
        </p>
      ) : (
        <dl className="ferro-detail-grid" data-testid="tenant-detail-grid">
          <dt>Slug</dt>
          <dd className="ferro-mono">{state.tenant.slug}</dd>

          <dt>Legal name</dt>
          <dd>{state.tenant.legalName}</dd>

          <dt>Display name</dt>
          <dd>{state.tenant.displayName}</dd>

          <dt>Vertical</dt>
          <dd>{state.tenant.vertical}</dd>

          <dt>Status</dt>
          <dd>{STATUS_LABEL[state.tenant.status] ?? state.tenant.status}</dd>

          <dt>Contract signed</dt>
          <dd>{formatDate(state.tenant.contractSignedAt)}</dd>

          <dt>Activated</dt>
          <dd>{formatDate(state.tenant.activatedAt)}</dd>

          <dt>Suspended</dt>
          <dd>{formatDate(state.tenant.suspendedAt)}</dd>

          <dt>Deactivated</dt>
          <dd>{formatDate(state.tenant.deactivatedAt)}</dd>

          <dt>Billing account</dt>
          <dd className="ferro-mono">{state.tenant.billingAccountId ?? '—'}</dd>

          <dt>Created</dt>
          <dd>{formatDate(state.tenant.createdAt)}</dd>

          <dt>Updated</dt>
          <dd>{formatDate(state.tenant.updatedAt)}</dd>
        </dl>
      )}

      <p className="ferro-muted" style={{ marginTop: '1.5rem', fontSize: '0.85rem' }}>
        Branding, modules, users, and audit-event drilldowns ship in a later workstream.
      </p>
    </section>
  );
}
